import React from "react";
import ErrataDisplay from "/src/helpers/components/errataDisplay.jsx";
import CancelledStampOverlay from "/src/helpers/components/cancelledStampOverlay.jsx";

const formatGbp = (value) =>
    typeof value === "number"
        ? `£${value.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
        : null;

const TicketIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
        <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a2 2 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
);

const HomepageExcursionRow = ({ item }) => {
    const isCancelled = item?.cancelled === true;
    const startDate = item.start ? new Date(item.start) : null;
    const isValidDate = (d) => d && d.getFullYear() > 1;
    const hasErratas = item.erratas && item.erratas.length > 0;
    const formatDate = (date) =>
        date.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short", year: "numeric" });

    // "4 hours" / "1 hour" / "2.5 hours" — only when iVector supplies a duration.
    const duration = item.durationHours > 0
        ? `${Number.isInteger(item.durationHours) ? item.durationHours : item.durationHours.toFixed(1).replace(/\.0$/, "")} hour${item.durationHours === 1 ? "" : "s"}`
        : null;

    const pax = [
        item.adults ? `${item.adults} Adult${item.adults !== 1 ? "s" : ""}` : null,
        item.children ? `${item.children} Child${item.children !== 1 ? "ren" : ""}` : null,
        item.infants ? `${item.infants} Infant${item.infants !== 1 ? "s" : ""}` : null,
    ].filter(Boolean).join(", ");

    return (
        <div className={`relative rounded-card shadow-card ${hasErratas ? "px-8 pt-6 pb-2" : "px-8 py-6"} ${isCancelled ? "bg-red-50 border border-red-200" : "bg-white"}`}>
            {isCancelled && <CancelledStampOverlay />}

            <div className="flex flex-wrap items-center gap-2 mb-3">
                <div className="text-emp"><TicketIcon /></div>
                <span className="text-sm font-semibold text-content_2 uppercase tracking-wide">Excursion</span>
                {item.extraType && (
                    <span className="text-xs bg-emp_soft text-emp_dark font-medium px-2.5 py-1 rounded-full">{item.extraType}</span>
                )}
                {item.isAncillary && (
                    <span className="text-xs bg-emp_deep text-white font-medium px-2.5 py-1 rounded-full">
                        Extras booking {item.ancillaryReference}
                    </span>
                )}
                {formatGbp(item.totalPrice) && (
                    <span className="text-sm font-semibold text-primary ml-auto">{formatGbp(item.totalPrice)}</span>
                )}
                {/* Only a cancellation *fee* is worth a badge; "free cancellation" is not advertised. */}
                {item.cancellable === true && item.cancellationCharge > 0 && (
                    <span className="text-xs font-medium px-2 py-0.5 rounded-full basis-full w-fit bg-orange_light text-orange">
                        Cancellation fee {formatGbp(item.cancellationCharge)}
                    </span>
                )}
            </div>

            <div className="flex flex-col gap-1">
                <p className="text-base font-medium text-primary">{item.name}</p>
                {isValidDate(startDate) && (
                    <p className="text-sm text-content_2">{formatDate(startDate)}</p>
                )}
                {(item.pickupTime || item.startTime || duration) && (
                    <p className="text-sm text-content_2 flex flex-wrap gap-x-4 gap-y-0.5">
                        {item.pickupTime && (
                            <span>Pick-up time: <span className="font-semibold text-primary">{item.pickupTime}</span></span>
                        )}
                        {item.startTime && (
                            <span>Excursion start time: <span className="font-semibold text-primary">{item.startTime}</span></span>
                        )}
                        {duration && (
                            <span>Duration: <span className="font-semibold text-primary">{duration}</span></span>
                        )}
                    </p>
                )}
                <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-content_3 mt-1">
                    {item.location && <span>{item.location}</span>}
                    {item.pickupPoint && <span>Pick-up point: {item.pickupPoint}</span>}
                    {pax && <span>{pax}</span>}
                    {item.supplierName && <span>{item.supplierName}</span>}
                </div>
            </div>

            {hasErratas && (
                <div className="mt-3 pt-3 border-t border-content_5">
                    <ErrataDisplay erratas={item.erratas} />
                </div>
            )}
        </div>
    );
};

export default HomepageExcursionRow;
